import React from "react";
import { Search, Loader2, X, MoreVertical, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../utils";
import { Dropdown, type DropdownItem } from "../dropdown";
import type { LucideIcon } from "lucide-react";

// =============================================================================
// ItemTable - Card-style container
// =============================================================================

interface ItemTableProps {
  children: React.ReactNode;
  className?: string;
}

export function ItemTable({ children, className }: ItemTableProps) {
  return (
    <div className={cn("border border-slate-200 rounded-xl bg-white overflow-hidden", className)}>
      {children}
    </div>
  );
}

// =============================================================================
// ItemTableSearch - Search bar integrated at top
// =============================================================================

interface ItemTableSearchProps {
  value: string;
  onChange: (value: string) => void;
  isLoading?: boolean;
  placeholder?: string;
  className?: string;
}

export function ItemTableSearch({
  value,
  onChange,
  isLoading,
  placeholder = "Search...",
  className,
}: ItemTableSearchProps) {
  return (
    <div className={cn("px-4 py-3 border-b border-slate-200", className)}>
      <div className="relative w-full max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-slate-400" />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full pl-9 pr-10 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 size-4 text-slate-400 animate-spin" />
        )}
        {!isLoading && value && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-500"
            aria-label="Clear search"
          >
            <X className="size-4" />
          </button>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// ItemTableHeader - Optional header row
// =============================================================================

interface ItemTableHeaderProps {
  children: React.ReactNode;
  className?: string;
}

export function ItemTableHeader({ children, className }: ItemTableHeaderProps) {
  return (
    <div
      className={cn(
        "grid items-center px-6 py-3 bg-slate-50 border-b border-slate-200 text-xs font-medium text-slate-500 uppercase tracking-wider",
        className
      )}
    >
      {children}
    </div>
  );
}

interface ItemTableHeaderCellProps {
  children?: React.ReactNode;
  className?: string;
}

export function ItemTableHeaderCell({ children, className }: ItemTableHeaderCellProps) {
  return <div className={cn("", className)}>{children}</div>;
}

// =============================================================================
// ItemTableBody - Container for rows
// =============================================================================

interface ItemTableBodyProps {
  children: React.ReactNode;
  className?: string;
}

export const ItemTableBody = React.forwardRef<HTMLDivElement, ItemTableBodyProps>(
  function ItemTableBody({ children, className }, ref) {
    return (
      <div ref={ref} className={cn("divide-y divide-slate-200", className)}>
        {children}
      </div>
    );
  }
);

// =============================================================================
// ItemTableRow - Individual row
// =============================================================================

interface ItemTableRowProps {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}

export function ItemTableRow({ children, className, onClick }: ItemTableRowProps) {
  return (
    <div
      className={cn(
        "grid items-center gap-4 px-6 py-4 hover:bg-slate-50 transition-colors",
        onClick && "cursor-pointer",
        className
      )}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

// =============================================================================
// ItemTableCell - Cell wrapper (direct grid child)
// =============================================================================

interface ItemTableCellProps {
  children?: React.ReactNode;
  className?: string;
}

export function ItemTableCell({ children, className }: ItemTableCellProps) {
  return <div className={cn("flex items-center gap-3 min-w-0 overflow-hidden", className)}>{children}</div>;
}

// =============================================================================
// ItemTableAvatar - Icon or thumbnail
// =============================================================================

interface ItemTableAvatarProps {
  icon?: LucideIcon;
  src?: string | null;
  alt?: string;
  className?: string;
}

export function ItemTableAvatar({ icon: Icon, src, alt = "", className }: ItemTableAvatarProps) {
  if (src) {
    return (
      <div className={cn("w-10 h-10 rounded-lg overflow-hidden flex-shrink-0", className)}>
        <img src={src} alt={alt} className="w-full h-full object-cover" />
      </div>
    );
  }

  if (Icon) {
    return (
      <div className={cn("w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0", className)}>
        <Icon className="w-5 h-5 text-slate-500" />
      </div>
    );
  }

  return null;
}

// =============================================================================
// ItemTableTitle - Primary text
// =============================================================================

interface ItemTableTitleProps {
  children: React.ReactNode;
  className?: string;
}

export function ItemTableTitle({ children, className }: ItemTableTitleProps) {
  return (
    <div className={cn("text-sm font-medium text-slate-900", className)}>
      {children}
    </div>
  );
}

// =============================================================================
// ItemTableSubtitle - Secondary text (truncated)
// =============================================================================

interface ItemTableSubtitleProps {
  children: React.ReactNode;
  className?: string;
}

export function ItemTableSubtitle({ children, className }: ItemTableSubtitleProps) {
  return (
    <div className={cn("text-sm text-slate-500 truncate mt-0.5", className)}>
      {children}
    </div>
  );
}

// =============================================================================
// ItemTableActions - Dropdown with MoreVertical trigger
// =============================================================================

interface ItemTableActionsProps {
  items: DropdownItem[];
  className?: string;
}

export function ItemTableActions({ items, className }: ItemTableActionsProps) {
  return (
    <div className={cn("", className)} onClick={(e) => e.stopPropagation()}>
      <Dropdown
        trigger={
          <button
            type="button"
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
        }
        items={items}
      />
    </div>
  );
}

// =============================================================================
// ItemTablePagination - Pagination at bottom
// =============================================================================

export interface ItemTablePaginationMeta {
  current_page: number;
  total_pages: number;
  total_count: number;
  per_page: number;
}

interface ItemTablePaginationProps {
  meta: ItemTablePaginationMeta;
  onPageChange: (page: number) => void;
  className?: string;
}

export function ItemTablePagination({ meta, onPageChange, className }: ItemTablePaginationProps) {
  const { current_page, total_pages, total_count, per_page } = meta;

  if (total_pages <= 1) return null;

  const start = (current_page - 1) * per_page + 1;
  const end = Math.min(current_page * per_page, total_count);

  const getPageNumbers = () => {
    const pages: (number | "...")[] = [];
    const showAround = 1;

    for (let i = 1; i <= total_pages; i++) {
      if (
        i === 1 ||
        i === total_pages ||
        (i >= current_page - showAround && i <= current_page + showAround)
      ) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== "...") {
        pages.push("...");
      }
    }

    return pages;
  };

  return (
    <div className={cn("flex items-center justify-between px-6 py-3 border-t border-slate-200 bg-slate-50", className)}>
      <p className="text-sm text-slate-500">
        Showing <span className="font-medium">{start}</span> to{" "}
        <span className="font-medium">{end}</span> of{" "}
        <span className="font-medium">{total_count}</span> results
      </p>

      <nav className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(current_page - 1)}
          disabled={current_page === 1}
          className={cn(
            "p-2 rounded-md text-slate-500 hover:bg-slate-200",
            current_page === 1 && "opacity-50 cursor-not-allowed hover:bg-transparent"
          )}
        >
          <ChevronLeft className="size-4" />
        </button>

        {getPageNumbers().map((page, idx) =>
          page === "..." ? (
            <span key={`ellipsis-${idx}`} className="px-2 text-slate-400">
              ...
            </span>
          ) : (
            <button
              key={page}
              type="button"
              onClick={() => onPageChange(page)}
              className={cn(
                "min-w-[2rem] h-8 px-2 rounded-md text-sm font-medium",
                page === current_page
                  ? "bg-blue-600 text-white"
                  : "text-slate-600 hover:bg-slate-200"
              )}
            >
              {page}
            </button>
          )
        )}

        <button
          type="button"
          onClick={() => onPageChange(current_page + 1)}
          disabled={current_page === total_pages}
          className={cn(
            "p-2 rounded-md text-slate-500 hover:bg-slate-200",
            current_page === total_pages && "opacity-50 cursor-not-allowed hover:bg-transparent"
          )}
        >
          <ChevronRight className="size-4" />
        </button>
      </nav>
    </div>
  );
}

// =============================================================================
// ItemTableEmpty - Empty state inside table
// =============================================================================

interface ItemTableEmptyProps {
  icon?: LucideIcon;
  title: string;
  description?: string;
  action?: React.ReactNode;
  className?: string;
}

export function ItemTableEmpty({ icon: Icon, title, description, action, className }: ItemTableEmptyProps) {
  return (
    <div className={cn("flex flex-col items-center justify-center py-12 px-6 text-center", className)}>
      {Icon && (
        <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-4">
          <Icon className="w-6 h-6 text-slate-400" />
        </div>
      )}
      <h3 className="text-sm font-medium text-slate-900 mb-1">{title}</h3>
      {description && <p className="text-sm text-slate-500 max-w-sm">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

// =============================================================================
// ItemTableLoading - Loading skeleton rows
// =============================================================================

interface ItemTableLoadingProps {
  rows?: number;
  className?: string;
}

export function ItemTableLoading({ rows = 3, className }: ItemTableLoadingProps) {
  return (
    <div className={cn("divide-y divide-slate-200", className)}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="px-6 py-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-slate-200 rounded-lg animate-pulse" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-slate-200 rounded w-1/3 animate-pulse" />
            <div className="h-3 bg-slate-200 rounded w-1/2 animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );
}
